<script lang="ts">
  import { onMount } from "svelte"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { registerNavigationGuard } from "@kontextwerk/web-sdk/core/routing/navigation"
  import { mdiContentSave, mdiRefresh } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import LoadingWrapper from "@kontextwerk/web-sdk/ui/layout/LoadingWrapper.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import type { OrgaViewFrameContext } from "../../../../context"
  import { controlContext, rejectControlContext } from "@kontextwerk/control-web-frame/shared/logic/authentication"
  import {
    captureMembershipSettings,
    listCapturedMembershipSettings,
    type DailyBriefingPreferences,
  } from "../../../../../../api/kehrblatt/membership-settings"
  import {
    fetchDailyBriefingSettings,
    type DailyBriefingSettings,
  } from "../../../../../../api/kehrblatt/daily-briefing"
  import { isSupportDelegationActive } from "../../../../../_shared/_logic/auth-context"
  import OrganizationSettings from "./_modules/organization-settings"
  import RecipientSettings from "./_modules/recipient-settings"

  const { viewFrameContext } = $props<{ viewFrameContext: OrgaViewFrameContext }>()
  const organizationId = $derived(
    viewFrameContext.organization?.id ?? viewFrameContext.organizationId ?? "",
  )
  const ownUserId = $derived($controlContext?.memberUserId ?? "")
  const canManageOrganization = $derived(
    $controlContext?.permissions.allows("core.org_settings", "manage") ?? false,
  )
  // Other members' briefing settings need membership management (daily_briefing admission).
  const canManageUsers = $derived(
    $controlContext?.permissions.allows("core.memberships", "manage") ?? false,
  )
  const canSendBriefing = $derived(
    $controlContext?.permissions.allows("kehrblatt.scheduling", "view") ?? false,
  )
  let settings = $state<DailyBriefingSettings | null>(null)
  let recipients = $state<
    Array<{
      membershipId: string
      userId: string
      label: string
      email: string
      preferences: DailyBriefingPreferences
      savePreferences:
        | ((
            value: DailyBriefingPreferences,
            signal: AbortSignal,
          ) => Promise<DailyBriefingPreferences>)
        | null
      signal: AbortSignal
      isCurrent: () => boolean
    }>
  >([])
  let loading = $state(true)
  let error = $state<string | null>(null)
  let refresh = $state(0)
  let generation = 0
  let currentContext = () => false
  type SettingsEditor = { saveChanges: () => Promise<boolean> }
  let organizationEditor = $state<SettingsEditor | undefined>()
  let recipientEditors = $state<Record<string, SettingsEditor | undefined>>({})
  let saving = $state(false)
  let saveError = $state("")
  let saveMessage = $state("")
  let formStates = $state<Record<string, { dirty: boolean; busy: boolean; valid?: boolean }>>({})
  const hasChanges = $derived(Object.values(formStates).some((state) => state.dirty))
  const busy = $derived(saving || Object.values(formStates).some((state) => state.busy))
  const invalidDraft = $derived(
    Object.values(formStates).some((state) => state.dirty && state.valid === false),
  )
  const organizationUnsettled = $derived(
    Boolean(formStates.organization?.dirty || formStates.organization?.busy),
  )
  onMount(() => {
    let active = true
    const unregister = registerNavigationGuard(
      "daily-briefing-settings",
      (_to, _options, resume) => {
        if (!currentContext()) return true

        if (busy) return false

        if (!hasChanges) return true

        const targetOrganization = organizationId
        void newApproval({
          title: "Ungespeicherte Einstellungen",
          description: "Möchten Sie die Seite verlassen und Ihre Änderungen verwerfen?",
          approvalLabel: "Verwerfen",
          declineLabel: "Weiter bearbeiten",
          approvalCallback: () => {
            if (active && currentContext() && !busy && organizationId === targetOrganization) {
              formStates = {}
              resume?.()
            }
          },
        })

        return false
      },
    )
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (currentContext() && (hasChanges || busy)) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", beforeUnload)

    return () => {
      active = false
      unregister()
      window.removeEventListener("beforeunload", beforeUnload)
    }
  })
  // effect-ok: async-load - each confirmed context owns configuration, members and cancellation.
  $effect(() => {
    const org = organizationId
    const own = ownUserId
    const context = $controlContext
    const session = context?.session
    // Members carry no email; the own address is the actual actor's, unknown during support.
    const ownEmail =
      context && !isSupportDelegationActive(session) && context.actor.id === own
        ? context.actor.email
        : ""
    refresh
    const requestId = ++generation
    const controller = new AbortController()
    settings = null
    recipients = []
    formStates = {}
    saving = false
    saveError = saveMessage = ""
    loading = true
    error = null
    const invalidate = () => {
      if (requestId !== generation) return

      generation++
      controller.abort()
      settings = null
      recipients = []
      formStates = {}
      saving = false
      loading = false
      saveError = saveMessage = ""
      error = "Zugang nicht bestaetigt."
    }
    let stop = () => {}
    currentContext = () => false
    const load = async () => {
      try {
        const binding = captureMembershipSettings(org)
        const current = () =>
          requestId === generation && !controller.signal.aborted && binding.current()
        currentContext = current
        binding.signal.addEventListener("abort", invalidate, { once: true })
        stop = () => binding.signal.removeEventListener("abort", invalidate)
        const configuration = await fetchDailyBriefingSettings(
          org,
          AbortSignal.any([controller.signal, binding.signal]),
        )

        if (!current()) return

        const members = await listCapturedMembershipSettings(binding, controller.signal)
        const rows: typeof recipients = members.map((member) => ({
          membershipId: member.membership_id,
          userId: member.user_id,
          label: member.display_name || "Mitgliedschaft",
          email: member.user_id === own ? ownEmail : "",
          preferences: member.settings.daily_briefing,
          signal: binding.signal,
          isCurrent: current,
          savePreferences: binding.canManage(member.membership_id)
            ? async (draft, signal) => {
                if (!current() || !binding.api)
                  throw new DOMException("Obsolete briefing context", "AbortError")

                try {
                  const result = await binding.api.saveBriefing(
                    member.membership_id,
                    draft,
                    AbortSignal.any([signal, controller.signal]),
                  )

                  if (
                    !current() ||
                    result.membership_id !== member.membership_id ||
                    result.user_id !== member.user_id
                  )
                    throw new DOMException("Obsolete briefing recipient", "AbortError")

                  return result.settings.daily_briefing
                } catch (error) {
                  if (
                    current() &&
                    [401, 403, 409].includes((error as { statusCode?: number })?.statusCode ?? 0)
                  )
                    rejectControlContext()

                  throw error
                }
              }
            : null,
        }))

        if (!current()) return

        settings = configuration
        recipients = rows
      } catch {
        if (requestId === generation) error = "Die Einstellungen konnten nicht geladen werden."
      } finally {
        if (requestId === generation) loading = false
      }
    }

    if (org && own && session) void load()
    else {
      loading = false
      error = "Zugang nicht bestaetigt."
    }

    return () => {
      generation++
      controller.abort()
      stop()
    }
  })

  /** Save the page's changed editors and keep failed drafts available for retry. */
  async function saveAllSettings() {
    if (!currentContext() || loading || busy || !hasChanges || invalidDraft) return

    const requestGeneration = generation
    const editors = [
      organizationEditor,
      ...recipients.map((recipient) => recipientEditors[recipient.membershipId]),
    ].filter((editor): editor is SettingsEditor => Boolean(editor))
    saving = true
    saveError = saveMessage = ""
    try {
      let allSaved = true
      for (const editor of editors) {
        const saved = await editor.saveChanges()

        if (requestGeneration !== generation) return

        allSaved = saved && allSaved
      }

      if (allSaved) {
        saveMessage = "Alle Änderungen gespeichert."
      } else {
        saveError =
          "Nicht alle Einstellungen konnten gespeichert werden. Bitte versuchen Sie es für die verbleibenden Änderungen erneut."
      }
    } finally {
      if (requestGeneration === generation) saving = false
    }
  }
</script>

<div class="daily-briefing">
  {#if error}
    <div class="state-block">
      <p role="alert">{error}</p>
      <CTA
        type="secondaryButton"
        content="Erneut versuchen"
        icon={mdiRefresh}
        darkMode={false}
        onClick={() => refresh++}
      />
    </div>
  {:else if loading || !settings}
    <div role="status" aria-label="Morgenübersicht wird geladen">
      <LoadingWrapper loading={true} progressLabel="Morgenübersicht wird geladen …" />
    </div>
  {:else}
    {#key `${organizationId}:${$controlContext?.session.session_id}:${$controlContext?.session.context_revision}`}
      <OrganizationSettings
        bind:this={organizationEditor}
        {organizationId}
        {settings}
        canManage={canManageOrganization}
        pageSaving={saving}
        onSaved={(value) => (settings = value)}
        onStateChange={(state) => {
          formStates.organization = state
        }}
      />
      <section class="recipients" aria-label="Empfänger">
        <SectionTitle
          title="Empfänger"
          description="Je Person festlegen, welche Inhalte in der Morgenübersicht erscheinen."
          brightBackground={true}
          small={true}
        />
        {#if recipients.length === 0}<p role="status">Keine Empfaenger verfuegbar.</p>{/if}
        {#each recipients as recipient (recipient.membershipId)}
          <RecipientSettings
            bind:this={recipientEditors[recipient.membershipId]}
            {organizationId}
            {recipient}
            timezone={settings.timezone}
            canSend={canSendBriefing && (recipient.userId === ownUserId || canManageUsers)}
            isSelf={recipient.userId === ownUserId}
            savePreferences={recipient.savePreferences}
            contextSignal={recipient.signal}
            isCurrent={recipient.isCurrent}
            {organizationUnsettled}
            pageSaving={saving}
            onStateChange={(state) => {
              formStates[recipient.membershipId] = state
            }}
          />
        {/each}
      </section>
      <div class="page-save-actions">
        {#if saveError}<p role="alert">{saveError}</p>{/if}
        {#if saveMessage && !hasChanges}<p role="status">{saveMessage}</p>{/if}
        <CTA
          type="primaryButton"
          content={saving ? "Wird gespeichert …" : "Speichern"}
          icon={mdiContentSave}
          darkMode={false}
          disabled={busy || !hasChanges || invalidDraft}
          onClick={saveAllSettings}
        />
      </div>
    {/key}
  {/if}
</div>

<style>
  .daily-briefing {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 2rem;
    min-width: 0;
  }
  .state-block p {
    margin: 0;
    color: var(--text-invers-100);
    line-height: 1.5;
  }
  .state-block {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  .recipients {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    min-width: 0;
  }
  .page-save-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  .page-save-actions p {
    margin: 0;
    color: var(--text-invers-100);
  }
</style>
